import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, debounceTime, filter, first, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { RecordsPlan, RecordsQuery } from '@mode-switch/query';
import { AccountFacade } from '@mode/accounts/contract';
import { FlamingoResponse, FlamingoTypes } from '@mode/shared/contract-common';
import { FlamingoClientService } from '@mode/shared/data-access-flamingo';
import { MetaTags, MetaTagsService, getReportOwnerUsername } from '@mode/shared/util-js';
import {
  dimensions,
  isPresent,
  makeQueryPlanMetadataId,
  makeQueryWithCalcsPlanMetadataId,
  makeWithCalcsRefId,
} from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { recordEvent } from '@mode/shared/util-tracing';
import {
  DataViewFacade,
  FlamingoActions,
  PlanMetadataActions,
  PlanMetadataFacade,
  SwitchViewActions,
} from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { combineLatest, from, of } from 'rxjs';
import { FlamingoRoot } from './flamingo.reducer';
import * as fromFlamingoSelectors from './flamingo.selectors';

const PAGING_DEBOUNCE_TIME = 150;
export const MAX_FLAMINGO_REQUESTS = new InjectionToken<number>('MAX_FLAMINGO_REQUESTS');

const adapter = new FlamingoAdapter();
const reportOwnerUsername = getReportOwnerUsername(window.document, false);

export class FlamingoError extends Error {
  constructor(public response: FlamingoResponse.FlamingoErrorResponse) {
    super(response.type);
    Object.setPrototypeOf(this, FlamingoError.prototype);
  }
}
@Injectable()
export class FlamingoEffects {
  queryRawDataset$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SwitchViewActions.queryRawDataset, SwitchViewActions.importedDatasetRefreshed),
      tap((action) => this.planMetadataFacade.initPagingIfUnset(makeQueryPlanMetadataId(action.queryToken))), // init the paging if not already set
      switchMap((action) =>
        this.preparePlanExecutionModel(action.queryToken).pipe(
          map(({ plan, queryRunToken }) =>
            FlamingoActions.executePlan({
              plan,
              refId: action.queryToken,
              queryRunToken,
              allowRefresh: true,
            })
          )
        )
      )
    );
  });

  // Similar to queryRawDataset
  // Used to refresh the data table that is used to display the data for an exported dataset
  queryExportedDataset$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SwitchViewActions.refreshEditorViewsSuccess),
      tap((action) => this.planMetadataFacade.initPagingIfUnset(makeQueryPlanMetadataId(action.exportedDatasetToken))), // init the paging if not already set
      switchMap((action) =>
        this.preparePlanExecutionModel(action.exportedDatasetToken).pipe(
          map(({ plan, queryRunToken }) =>
            FlamingoActions.executePlan({
              plan,
              refId: action.exportedDatasetToken,
              queryRunToken,
              allowRefresh: true,
            })
          )
        )
      )
    );
  });
  /** Common method to prepare the plan execution for raw and exported datasets
   * @param {string}  datasetToken - query/dataset token
   * @returns the Flamingo execution plan and query run token
   */
  preparePlanExecutionModel(datasetToken: string) {
    return this.dataViewFacade.createRunReadyByQueryToken(datasetToken).pipe(
      filter(isPresent),
      filter((ready) => ready),
      switchMap((_) => this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(datasetToken).pipe(first())),
      switchMap((runToken) =>
        this.dataViewFacade.createColumnsSwitchModelByQueryToken(datasetToken).pipe(
          first(),
          filter(isPresent),
          map((model) => ({ runToken, model }))
        )
      ),
      withLatestFrom(this.planMetadataFacade.createOffsetSelector(makeQueryPlanMetadataId(datasetToken))),
      map(([{ runToken, model }, offset]) => {
        const plan = RecordsPlan.make(
          adapter,
          model,
          datasetToken,
          RecordsQuery.make({
            limit: 100,
            offset: offset,
            fields: dimensions(adapter, model, datasetToken),
          })
        );

        return {
          plan: plan,
          queryRunToken: runToken,
        };
      })
    );
  }

  queryDatasetWithCalcs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SwitchViewActions.queryDatasetWithCalcs),
      tap((action) => this.planMetadataFacade.initPagingIfUnset(makeQueryWithCalcsPlanMetadataId(action.queryToken))), // init the paging if not already set
      switchMap((action) =>
        this.dataViewFacade.createRunReadyByQueryToken(action.queryToken).pipe(
          filter(isPresent),
          filter((ready) => ready),
          switchMap((_) =>
            this.dataViewFacade.createSwitchViewTokenByQueryToken(action.queryToken).pipe(first(), filter(isPresent))
          ),
          switchMap((switchViewToken) =>
            this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(action.queryToken).pipe(
              first(),
              switchMap((runToken) =>
                this.dataViewFacade
                  .createColumnsAndAttrsSwitchModelByQueryToken(action.queryToken, switchViewToken)
                  .pipe(
                    first(),
                    filter(isPresent),
                    map((model) => ({ runToken, model }))
                  )
              ),
              withLatestFrom(
                this.planMetadataFacade.createOffsetSelector(makeQueryWithCalcsPlanMetadataId(action.queryToken))
              ),
              map(([{ runToken, model }, offset]) => {
                const plan = RecordsPlan.make(
                  adapter,
                  model,
                  action.queryToken,
                  RecordsQuery.make({
                    limit: 100,
                    offset: offset,
                    fields: dimensions(adapter, model, action.queryToken),
                  })
                );

                return FlamingoActions.executePlan({
                  plan: plan,
                  refId: makeWithCalcsRefId(action.queryToken),
                  queryRunToken: runToken,
                  allowRefresh: true,
                });
              })
            )
          )
        )
      )
    );
  });

  setOffsetRawResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanMetadataActions.setOffsetRawResults),
      debounceTime(PAGING_DEBOUNCE_TIME),
      switchMap((action) =>
        this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(action.queryToken).pipe(
          first(),
          switchMap((runToken) =>
            this.dataViewFacade.createColumnsSwitchModelByQueryToken(action.queryToken).pipe(
              first(),
              filter(isPresent),
              map((model) => ({ runToken, model }))
            )
          ),
          map(({ runToken, model }) => {
            const plan = RecordsPlan.make(
              adapter,
              model,
              action.queryToken,
              RecordsQuery.make({
                limit: 100,
                offset: action.offset,
                fields: dimensions(adapter, model, action.queryToken),
              })
            );

            return FlamingoActions.executePagePlan({
              plan: plan,
              refId: action.queryToken,
              queryRunToken: runToken,
              allowRefresh: true,
            });
          })
        )
      )
    );
  });

  setOffsetWithCalcsResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanMetadataActions.setOffsetWithCalcs),
      debounceTime(PAGING_DEBOUNCE_TIME),
      switchMap((action) =>
        this.dataViewFacade.createSwitchViewTokenByQueryToken(action.queryToken).pipe(
          filter(isPresent),
          first(),
          map((switchViewToken) => ({ action, switchViewToken }))
        )
      ),
      switchMap(({ action, switchViewToken }) =>
        this.dataViewFacade.createQueryRunTokenByQueryTokenNoEmpty(action.queryToken).pipe(
          first(),
          switchMap((runToken) =>
            this.dataViewFacade.createColumnsAndAttrsSwitchModelByQueryToken(action.queryToken, switchViewToken).pipe(
              first(),
              filter(isPresent),
              map((model) => ({ runToken, model }))
            )
          ),
          map(({ runToken, model }) => {
            const plan = RecordsPlan.make(
              adapter,
              model,
              action.queryToken,
              RecordsQuery.make({
                limit: 100,
                offset: action.offset,
                fields: dimensions(adapter, model, action.queryToken),
              })
            );

            return FlamingoActions.executePagePlan({
              plan: plan,
              refId: makeWithCalcsRefId(action.queryToken),
              queryRunToken: runToken,
              allowRefresh: true,
            });
          })
        )
      )
    );
  });

  executePlan$ = createEffect(() => {
    // Execute plan effect for glorious future, filtering for GF routes
    // This effect has an `$evalAsync()` after the action is dispatched, not sure what this affects, but
    // for now, leaving this effect here unless we know it can be removed and migrated to `flamingo-gf.effects`
    return this.actions$.pipe(
      ofType(FlamingoActions.executePlan, FlamingoActions.executePagePlan),
      mergeMap((action) =>
        combineLatest([
          this.accountFacade.reportOwnerAccountDatasetLimit$,
          this.dataViewFacade.getDatasetResultByQueryRunToken(action.queryRunToken).pipe(filter(isPresent)),
        ]).pipe(
          first(),
          map(([limit, datasetResult]) => ({ action, limit, datasetResult }))
        )
      ),
      withLatestFrom(
        this.store.select(fromFlamingoSelectors.getSignatures),
        this.store.select(fromFlamingoSelectors.getCompletedPlanRefs)
      ),
      mergeMap(([{ action, limit, datasetResult }, switchViewSignatures, prevPlanRefs]) => {
        const planFailed = of(
          FlamingoActions.planFailed({
            planId: action.plan.id,
            refId: action.refId,
            response: {
              type: FlamingoResponse.ResponseType.SizeLimit,
              limit,
              actual: (datasetResult.bytes as number) || (datasetResult.contentLength as number),
            },
          })
        );

        if (datasetResult.bytes !== null && limit) {
          if (datasetResult.bytes >= 0 && datasetResult.bytes > limit) {
            return planFailed;
          }
        } else if (limit && limit * 2.5 < (datasetResult.contentLength as number)) {
          return planFailed;
        }
        return this.handleExecutePlanSelection(
          switchViewSignatures[datasetResult.switchViewToken],
          prevPlanRefs,
          action,
          datasetResult.switchViewToken,
          datasetResult.dbtMetricToken
        );
      }, this.maxRequests)
    );
  });

  executeExportPlan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FlamingoActions.executeExportPlan),
      withLatestFrom(
        this.store.select(fromFlamingoSelectors.getSignatures),
        this.store.select(fromFlamingoSelectors.getCompletedPlanRefs)
      ),
      mergeMap(([action, signatures, planRefs]) => {
        return this.dataViewFacade
          .getDatasetResultByQueryRunToken(action.queryRunToken)
          .pipe(filter(isPresent), first())
          .pipe(
            mergeMap((datasetResult) => {
              const FlamingoHost: string = this.meta.getTag(MetaTags.FlamingoHost) as string;
              return from(
                this.flamingoClient.csvSelect(
                  FlamingoHost,
                  signatures[datasetResult.switchViewToken],
                  datasetResult.queryToken,
                  action.plan,
                  action.refId
                )
              ).pipe(
                map((url) =>
                  FlamingoActions.exportPlanSuccess({
                    request: action.plan,
                    urlOrBlob: url,
                    refId: action.refId,
                    prevPlanId: planRefs[action.refId],
                  })
                ),
                catchError((err, caught) => {
                  if (err instanceof HttpErrorResponse) {
                    if (err.status === 412 && action.allowRefresh) {
                      return of(
                        FlamingoActions.refreshSwitchViewSignature({
                          executePlanAction: action,
                          reportOwner: reportOwnerUsername as string,
                          switchViewToken: datasetResult.switchViewToken,
                        })
                      );
                    }
                  }

                  return of(
                    FlamingoActions.exportPlanFailed({
                      planId: action.plan.id,
                      refId: action.refId,
                      response: {
                        type: FlamingoResponse.ResponseType.UnknownError,
                        status: -1, // can't fill this out
                        message: err.message,
                      },
                    })
                  );
                })
              );
            })
          );
      })
    );
  });

  /**
   * Handles the selection for executing flamingo plans.
   *
   * Since the actual action is needed, not abstracting properties off of it. This method is explicitly
   * for handling these execution actions.
   */
  private handleExecutePlanSelection(
    signature: string,
    prevPlanRefs: FlamingoTypes.FlamingoPlanRef,
    action: ReturnType<typeof FlamingoActions.executePlan | typeof FlamingoActions.executePagePlan>,
    switchViewToken: string,
    dbtMetricToken?: string
  ) {
    const vizToken = action.type === FlamingoActions.executePlan.type ? action.vizToken : '';
    recordEvent('flamingo-query', 'execute-plan', {
      traceId: action.plan.id,
      plan: action.plan,
      refId: action.refId,
      queryRunToken: action.queryRunToken,
      switchViewToken,
    });
    return this.select(signature, action.queryRunToken, action.plan, vizToken, dbtMetricToken)
      .then((response) => {
        recordEvent('flamingo-query', 'execute-plan-success', {
          traceId: action.plan.id,
          plan: action.plan,
          refId: action.refId,
          queryRunToken: action.queryRunToken,
          switchViewToken,
          type: action.type,
        });

        switch (response.type) {
          case FlamingoResponse.ResponseType.SelectSuccess: {
            return action.type === FlamingoActions.executePlan.type
              ? FlamingoActions.planSuccess({
                  result: response.result,
                  request: action.plan,
                  refId: action.refId,
                  prevPlanId: prevPlanRefs[action.refId],
                })
              : FlamingoActions.pagingSuccess({
                  result: response.result,
                  request: action.plan as FlamingoTypes.ModeRecordsPlan,
                  refId: action.refId,
                  prevPlanId: prevPlanRefs[action.refId],
                });
          }
        }
      })
      .catch((err) => {
        recordEvent('flamingo-query', 'execute-plan-failed', {
          traceId: action.plan.id,
          refId: action.refId,
          plan: action.plan,
          queryRunToken: action.queryRunToken,
          type: action.type,
          switchViewToken,
          err,
        });

        if (err instanceof FlamingoError) {
          if (err.response.type === FlamingoResponse.ResponseType.SignatureExpired && action.allowRefresh) {
            return FlamingoActions.refreshSwitchViewSignature({
              executePlanAction: action,
              reportOwner: reportOwnerUsername as string,
              switchViewToken: switchViewToken,
            });
          }

          return FlamingoActions.planFailed({ planId: action.plan.id, refId: action.refId, response: err.response });
        } else {
          // Shouldn't arrive here unless some catastrophic issue happens
          return FlamingoActions.planFailed({
            planId: action.plan.id,
            refId: action.refId,
            response: {
              type: FlamingoResponse.ResponseType.UnknownError,
              status: -1, // can't fill this out
              message: err.message,
            },
          });
        }
      });
  }

  public async select(
    signature: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    vizToken?: string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.SelectSuccessResponse> {
    this.observabilityService.startChildSpan(ObservabilityEvents.HELIX, ObservabilityEvents.QUERY_RUN);
    const doReadySelect = async (): Promise<FlamingoResponse.SelectSuccessResponse> => {
      const FlamingoHost: string = this.meta.getTag(MetaTags.FlamingoHost) as string;
      const readyResponse = await this.flamingoClient.ready(FlamingoHost, signature, datasetName, dbtMetricToken);

      switch (readyResponse.type) {
        case FlamingoResponse.ResponseType.DatasetFailed: {
          throw new FlamingoError(readyResponse);
        }
        case FlamingoResponse.ResponseType.SignatureExpired: {
          throw new FlamingoError(readyResponse);
        }
        case FlamingoResponse.ResponseType.ReadyCommitted: {
          // ready to select
          const selectResponse = await this.flamingoClient.select(
            FlamingoHost,
            signature,
            datasetName,
            plan,
            vizToken,
            dbtMetricToken
          );
          this.observabilityService.endChildSpan(ObservabilityEvents.HELIX);

          switch (selectResponse.type) {
            case FlamingoResponse.ResponseType.SelectSuccess: {
              return selectResponse;
            }
            default: {
              throw new FlamingoError(selectResponse);
            }
          }
        }
        case FlamingoResponse.ResponseType.DatasetNotFound: // falls through
        default: {
          throw new FlamingoError(readyResponse);
        }
      }
    };

    return await doReadySelect();
  }

  constructor(
    private actions$: Actions,
    private dataViewFacade: DataViewFacade,
    private planMetadataFacade: PlanMetadataFacade,
    private flamingoClient: FlamingoClientService,
    private meta: MetaTagsService,
    private store: Store<FlamingoRoot>,
    private accountFacade: AccountFacade,
    private observabilityService: ObservabilityService,
    @Inject(MAX_FLAMINGO_REQUESTS) private maxRequests: number
  ) {}
}
